import {
  createContext,
  use,
  useCallback,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";

import type { components } from "@/src/api/api";
import { readStorage, writeStorage } from "@/src/libs/storage";

/** Même forme que côté app : l'utilisateur connecté, jeton compris. */
export type Session = components["schemas"]["FrontUserSchemaOutput"];

/** Clé de stockage — identique à celle de wargame-app, par cohérence. */
const SESSION_KEY = "session";

type AuthContextProps = {
  session: Session | null;
  setSession: (_session: Session | null) => void;
  signOut: () => void;
  /**
   * Toujours `false` sur le web : `localStorage` est synchrone, la session est
   * donc connue dès le premier render. Le champ existe pour garder la même
   * signature que le contexte de l'app.
   */
  isLoading: boolean;
};

const AuthContext = createContext<AuthContextProps | null>(null);

export function useSession(): AuthContextProps {
  const authContext = use(AuthContext);

  if (!authContext) {
    throw new Error("useSession must be wrapped in a <SessionProvider />");
  }

  return authContext;
}

function readStoredSession(): Session | null {
  const raw = readStorage(SESSION_KEY);
  if (!raw) return null;

  try {
    return JSON.parse(raw) as Session;
  } catch {
    // JSON illisible : on purge plutôt que de traîner un état inexploitable.
    writeStorage(SESSION_KEY, null);
    return null;
  }
}

export function SessionProvider({ children }: PropsWithChildren) {
  // Initialiseur paresseux : la lecture du stockage n'a lieu qu'au montage,
  // pas à chaque render.
  const [session, setSessionState] = useState<Session | null>(
    readStoredSession,
  );

  const setSession = useCallback((next: Session | null) => {
    writeStorage(SESSION_KEY, next ? JSON.stringify(next) : null);
    setSessionState(next);
  }, []);

  const signOut = useCallback(() => {
    setSession(null);
  }, [setSession]);

  const value = useMemo<AuthContextProps>(
    () => ({ session, setSession, signOut, isLoading: false }),
    [session, setSession, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
